// file: pharmakon/client/src/config/shortcuts.config.js
/**
 * @fileoverview PHARMAKON — Keyboard shortcut registry (single source of truth).
 * @description The POS screens each mount a once-registered keydown listener with
 *   their own bindings. This registry is the ONE place those bindings are
 *   declared so any legend rendered elsewhere (the Caissier dashboard card, the
 *   F1 cheat-sheet overlay) stays in lock-step with the real handlers instead
 *   of drifting into stale copy.
 *
 *   When you add/change a binding in a POS screen's listener, update it HERE too —
 *   the screens' `keydown` handlers are hand-written for freshness (reading this
 *   map at runtime is not enforced), but the legend components DO read from here,
 *   so the documented shortcuts can never silently lie. The completeness contract
 *   is enforced by src/config/__tests__/shortcuts.pos.test.js, which source-scans
 *   the POS pages for every key declared below.
 *
 * Sources kept in sync with:
 *   - CaisseLibre.jsx        → the CAISSE_LIBRE map (once-mounted listener)
 *   - VenteChifa.jsx         → the VENTE_CHIFA map (POS-03 / POS-08 · AUD-014)
 *   - Dashboards.jsx (caissier) → the CAISSIER_DASHBOARD map (quick-nav keys)
 */

/**
 * @typedef {Object} Shortcut
 * @property {string} key   The keyboard key as shown to the user ("F2", "+ / −",
 *                          "1 · 2 · 3 · 4"…). For plain bindings this matches
 *                          KeyboardEvent.key exactly.
 * @property {string} label Short human label for the action (French, UI copy).
 * @property {string} [group] Cheat-sheet grouping ("Général" | "Panier" |
 *                          "Vente" | "Paiement" | "Navigation").
 */

import { comboId, formatCombo, parseCombo } from "../lib/key-combo.js";
import { RACCOURCI_PANNEAU } from "../components/alertes/alerte-equipe.config.js";

const S = (key, label, group) => Object.freeze({ key, label, group });

/** @type {Readonly<Record<string, ReadonlyArray<Shortcut>>>} */
export const POS_SHORTCUTS = Object.freeze({
  // Caisse Vente Libre (comptant / crédit).
  CAISSE_LIBRE: Object.freeze([
    S("F1", "Afficher / masquer l'aide raccourcis", "Général"),
    S("F2", "Rechercher un produit", "Panier"),
    // X XWP-18 (XA-11, XD-46) — additive: the multiwindow keys; F3 now opens the next client's window
    // (the basket stays reserved in its own window, replacing « Mettre en attente »).
    S("F3", "Nouvelle fenêtre (client suivant)", "Fenêtres"),
    S("Maj + F1 / F2 / F3", "Aller à la fenêtre 1 / 2 / 3", "Fenêtres"),
    S("Maj + Suppr", "Fermer la fenêtre courante", "Fenêtres"),
    S("F4", "Sélectionner le client", "Vente"),
    // X XWP-22 (XA-11, additive) — the client's instances CHIFA, to pay one in vente libre (§8).
    S("Alt + N", "Instances du client (envoyer en vente libre)", "Vente"),
    // X XWP-23 (XA-11, additive) — the review form of the poste's CHIFA facture (§4.2: no review, no client).
    S("Alt + H", "Créer le client depuis la facture CHIFA", "Vente"),
    // X XWP-74 (XA-11, additive) — Stock & lots of the selected line, in a new window (the ticket stays).
    S("Alt + K", "Stock & lots du produit, dans une nouvelle fenêtre", "Fenêtres"),
    // Trésorerie TWP-59 (additive) — the one W7 door: an entrée / sortie d'espèces of the session's drawer
    // (Alt+E / Alt+D are the browser's — RESERVED_COMBOS; XWP-48 extends this map).
    S("Alt + J", "Entrée / sortie d'espèces du tiroir", "Caisse"),
    // X XWP-19 (XA-11) — one remise field, « % | DA » (Espace on an empty field switches the unit).
    S("F6", "Saisir la remise (% ou DA)", "Vente"),
    // X XWP-96 (XA-11, additive) — « Sélectionner depuis autres lots » (the Propriétaire's setting): F7 and Alt+L
    // X XWP-98 (XO-26 (2); review, XD-314 amended) — the same « Choisir le lot » dialog: a click on the line
    // (« Le caissier choisit le lot »), the line's « Sélectionner depuis autres lots » (its own setting);
    // F7 / Alt+L under either
    S("F7", "Choisir le lot de la ligne sélectionnée (clic sur la ligne, ou « Autres lots » selon les réglages)", "Panier"),
    S("Alt + L", "Choisir le lot de la ligne sélectionnée (clic sur la ligne, ou « Autres lots » selon les réglages)", "Panier"),
    S("F8", "Récupérer l'ordonnance (DME)", "Vente"),
    S("↑ / ↓", "Sélectionner une ligne du panier", "Panier"),
    S("+ / −", "Quantité de la ligne sélectionnée", "Panier"),
    S("Suppr", "Retirer la ligne sélectionnée", "Panier"),
    S("Entrée", "Ajuster la ligne signalée au stock disponible", "Panier"),
    S("F9", "Valider la vente (choix d'encaissement)", "Paiement"),
    S("1 · 2 · 3 · 4", "Encaisser : Espèces · Carte · Chèque · Virement", "Paiement"),
    S("C / D / I", "Encaisser : CHIFA · Crédit (dette) · Instance", "Paiement"),
    S("Échap", "Fermer la fenêtre / vider la recherche", "Général"),
  ]),
  // Vente CHIFA (tiers-payant CNAS).
  VENTE_CHIFA: Object.freeze([
    S("F1", "Afficher / masquer l'aide raccourcis", "Général"),
    S("F2", "Rechercher un produit", "Panier"),
    S("F4", "Envoyer vers Chifa Officine", "Paiement"),
    S("F6", "Saisir le N° de carte CNAS", "Vente"),
    S("F7", "Basculer le taux PEC (τ 80 % / 100 %)", "Vente"),
    S("F8", "Récupérer l'ordonnance (DME)", "Vente"),
    S("↑ / ↓", "Sélectionner une ligne (CHIFA puis Libre)", "Panier"),
    // Batch-1 §5 — basculer la ligne sélectionnée entre les deux tableaux.
    // Alt + flèche, et non une touche nue : les flèches nues déplacent déjà la
    // sélection, et le comptoir ne réapprend pas un geste qu'il connaît.
    S("Alt + ↑ / ↓", "Basculer la ligne : remboursable ↔ vente libre", "Panier"),
    // X XWP-96 (XA-11, additive) — « Sélectionner depuis autres lots » (F7 stays the PEC rate here);
    // X XWP-98 (XO-26 (2)) — a click on the line opens the same dialog
    S("Alt + L", "Choisir le lot de la ligne sélectionnée (clic sur la ligne, ou « Autres lots » selon les réglages)", "Panier"),
    S("+ / −", "Quantité de la ligne sélectionnée", "Panier"),
    S("Suppr", "Retirer la ligne sélectionnée", "Panier"),
    // Trésorerie TWP-59 (additive) — the one W7 door, the same key as /caisse.
    S("Alt + J", "Entrée / sortie d'espèces du tiroir", "Caisse"),
    S("F9", "Valider la vente (déstockage)", "Paiement"),
    S("Échap", "Fermer la fenêtre / vider la recherche", "Général"),
  ]),
  // Caissier dashboard ("Ma caisse") — quick-nav to the two tills.
  CAISSIER_DASHBOARD: Object.freeze([
    S("F1", "Afficher / masquer l'aide raccourcis", "Général"),
    S("F2", "Démarrer une vente libre", "Navigation"),
    S("F3", "Ouvrir la vente CHIFA", "Navigation"),
    // Trésorerie TWP-19 (additive) — the counter session from the keyboard (TA-71 (6)).
    S("F7", "Ouvrir ma session / clôturer ma session", "Session"),
    S("F8", "Imprimer le rapport X de ma session", "Session"),
    // Trésorerie TWP-22 (additive) — the « Ma caisse » card's gestures (UX 02 §13.1), bound by the card
    // through the registry map TRESO_CAISSIER (bare letters never fire in a text field).
    S("E · D · T", "Ma caisse : encaisser · décaisser · transférer", "Trésorerie"),
    S("Alt + T", "Transférer depuis ma caisse", "Trésorerie"),
  ]),
  // Réseau de confrères (transferts inter-pharmacies) — l'annuaire et les
  // invitations. Les touches REPRENNENT délibérément la carte du comptoir :
  // F2 cherche, F4 choisit le confrère, F9 valide le geste de l'état courant.
  // La mémoire musculaire du comptoir se transfère sans réapprentissage.
  CONFRERES: Object.freeze([
    S("F1", "Afficher / masquer l'aide raccourcis", "Général"),
    S("F2", "Rechercher un confrère dans la liste", "Navigation"),
    S("F4", "Inviter un confrère par e-mail", "Confrères"),
    S("↑ / ↓", "Sélectionner un confrère de la liste", "Navigation"),
    S("F9", "Accepter l'invitation sélectionnée", "Confrères"),
    S("Échap", "Fermer la fenêtre / vider la recherche", "Général"),
  ]),
});

/**
 * Flatten every POS shortcut into a de-duplicated legend (one row per key), so a
 * dashboard can list the real bindings without hardcoding them. Where the same
 * key means different things per screen, the screen name is appended for clarity;
 * where it is identical everywhere (F1 / Échap), it appears once, unqualified.
 *
 * @returns {ReadonlyArray<{ key: string, label: string }>}
 */
export function posShortcutLegend() {
  /** @type {Map<string, Map<string, string>>} */
  const byKey = new Map();
  const screenLabel = {
    CAISSE_LIBRE: "Vente libre",
    VENTE_CHIFA: "CHIFA",
    CAISSIER_DASHBOARD: "Ma caisse",
    CONFRERES: "Confrères",
  };

  for (const [screen, shortcuts] of Object.entries(POS_SHORTCUTS)) {
    for (const { key, label } of shortcuts) {
      if (!byKey.has(key)) byKey.set(key, new Map());
      byKey.get(key).set(label, screenLabel[screen] ?? screen);
    }
  }

  return Array.from(byKey.entries()).map(([key, labels]) => {
    // One meaning across screens → show it plain. Divergent meanings → qualify
    // each with its screen so the caissier knows which till it applies to.
    if (labels.size === 1) {
      const [label] = labels.keys();
      return { key, label };
    }
    const parts = Array.from(labels.entries()).map(
      ([label, screen]) => `${label} (${screen})`,
    );
    return { key, label: parts.join(" · ") };
  });
}

/**
 * The grouped shortcut list for ONE screen — consumed by the F1 cheat-sheet
 * overlay (components/pos/ShortcutCheatSheet.jsx). Groups preserve their first-
 * seen order so the sheet reads top-down the way the screen is used
 * (Général → Panier → Vente → Paiement).
 *
 * @param {keyof typeof POS_SHORTCUTS | string} screen
 * @returns {ReadonlyArray<{ group: string, shortcuts: ReadonlyArray<Shortcut> }>}
 */
export function posScreenShortcutGroups(screen) {
  const list = POS_SHORTCUTS[screen] ?? [];
  /** @type {Map<string, Shortcut[]>} */
  const grouped = new Map();
  for (const s of list) {
    const g = s.group ?? "Raccourcis";
    if (!grouped.has(g)) grouped.set(g, []);
    grouped.get(g).push(s);
  }
  return Array.from(grouped.entries()).map(([group, shortcuts]) => ({
    group,
    shortcuts,
  }));
}

// ═════════════════════════════════════════════════════════════════════════════
// Keyboard layer of the whole pharmacy shell (refonte WP-17 · F2 —
// design/ux/01_foundations.md §4, 02_UX_DESIGN.md §2). EXTENDS the registry:
// POS_SHORTCUTS above stays byte-identical and keeps its hand-written page
// listeners (the POS screens register as « legacy » screens, see
// hooks/useShortcuts.js), while every other screen is bound by the ONE
// capture-phase dispatcher (lib/shortcut-dispatcher.js) from the entries
// below. The same entry feeds the handler, the F1 sheet and the ActionBar
// chip, so the documentation can never drift from the binding.
//
// Combination notation: lib/key-combo.js (« Ctrl+S », « Alt+Digit3 »,
// « Escape »…); `key` is its French display (« Ctrl + S », « Échap »).
// ═════════════════════════════════════════════════════════════════════════════

/**
 * @typedef {Object} KeyBinding
 * @property {string}   id        handler id (verb or action id)
 * @property {ReadonlyArray<string>} combos canonical combinations (lib/key-combo.js)
 * @property {string}   key       display (« Ctrl + K », « Alt + 1 … 9 »)
 * @property {string}   label     French label (F1 sheet, ActionBar)
 * @property {string}   group     F1 sheet group
 * @property {boolean}  nav       a global navigation key (suspended by modals
 *                                and by `suppressGlobal: ["nav"]`, §4.3 rules 5-6)
 * @property {boolean}  commit    a commit verb: a held key never repeats it (rule 3)
 * @property {boolean}  bar       shown in the screen's ActionBar
 * @property {boolean}  primary   the ActionBar's primary verb (last, lg)
 * @property {string}   [shortLabel] ActionBar label under 1280 px
 * @property {string}   [icon]
 * @property {boolean}  [danger]
 */

const BINDING_OVERRIDABLE = Object.freeze([
  "label",
  "shortLabel",
  "group",
  "bar",
  "primary",
  "icon",
  "danger",
]);

/** Build one binding. Combinations are validated at module load. */
function K(id, combos, label, group, opts = {}) {
  const list = (Array.isArray(combos) ? combos : [combos]).map((c) => comboId(parseCombo(c)));
  return Object.freeze({
    id,
    combos: Object.freeze(list),
    key: opts.display ?? list.map((c) => formatCombo(c)).join(" / "),
    label,
    group,
    nav: Boolean(opts.nav),
    commit: Boolean(opts.commit),
    bar: Boolean(opts.bar),
    primary: Boolean(opts.primary),
    ...(opts.shortLabel ? { shortLabel: opts.shortLabel } : {}),
    ...(opts.icon ? { icon: opts.icon } : {}),
    ...(opts.danger ? { danger: true } : {}),
  });
}

function withOverrides(base, overrides = {}) {
  const extra = {};
  for (const k of Object.keys(overrides)) {
    if (!BINDING_OVERRIDABLE.includes(k)) {
      throw new Error(`shortcuts: « ${k} » cannot be overridden on ${base.id} (a verb keeps its key everywhere)`);
    }
    extra[k] = overrides[k];
  }
  return Object.freeze({ ...base, ...extra });
}

/** Alt+1 … Alt+9 on KeyboardEvent.code (AZERTY: the digits need Maj). */
const CATEGORY_COMBOS = Object.freeze(
  Array.from({ length: 9 }, (_, i) => `Alt+Digit${i + 1}`),
);

/** Ctrl + Maj + A — the existing alertes-équipe binding, now in the registry. */
const ALERTES_COMBO = [
  RACCOURCI_PANNEAU.ctrl && "Ctrl",
  RACCOURCI_PANNEAU.shift && "Shift",
  String(RACCOURCI_PANNEAU.key).toUpperCase(),
]
  .filter(Boolean)
  .join("+");

/** ux/01 §4.1 — every authenticated screen of the pharmacy shell. */
export const GLOBAL_SHORTCUTS = Object.freeze([
  K("AIDE", "F1", "Afficher / masquer l'aide raccourcis", "Général"),
  K("PALETTE", "Ctrl+K", "Aller à… (pages, produits)", "Navigation", { nav: true }),
  K("MENU", "Alt+M", "Aller au menu", "Navigation", { nav: true }),
  K("CATEGORIE", CATEGORY_COMBOS, "Ouvrir une catégorie du menu", "Navigation", {
    nav: true,
    display: "Alt + 1 … 9",
  }),
  K("ALERTES_EQUIPE", ALERTES_COMBO, "Console alertes équipe", "Général", {
    display: RACCOURCI_PANNEAU.libelle,
  }),
  K("FERMER", "Escape", "Fermer la fenêtre, le menu ou le panneau", "Général"),
  // X XWP-74 (CP §15.20, XO-14) — bound only in a window opened by « ouvrir dans une nouvelle fenêtre »
  // (components/shell/RetourFenetrePrecedente.jsx); it never navigates, so it is not a nav key.
  K("RETOUR_FENETRE", "Alt+W", "Retourner vers la fenêtre précédente", "Navigation"),
]);

/** Global ids a screen map may never rebind (02_UX_DESIGN §2.6). */
export const GLOBAL_ONLY_IDS = Object.freeze(["PALETTE", "MENU", "CATEGORIE", "ALERTES_EQUIPE", "RETOUR_FENETRE"]);

/**
 * X XWP-74 — « Stock & lots » of the product being worked on, in a new window (the form stays open):
 * the same key and wording on every source form that offers it (S1, avoir and BC editors, the caisse).
 */
const STOCK_NOUVELLE_FENETRE = Object.freeze({
  combo: "Alt+K",
  label: "Stock & lots du produit, dans une nouvelle fenêtre",
  group: "Fenêtres",
});
const stockNouvelleFenetre = (opts = {}) =>
  K("STOCK_LOTS", STOCK_NOUVELLE_FENETRE.combo, STOCK_NOUVELLE_FENETRE.label, STOCK_NOUVELLE_FENETRE.group, {
    icon: "boxes",
    shortLabel: "Stock",
    ...opts,
  });

/** Groups a screen may suppress while it holds unsaved work (§4.3 rule 6). */
export const SUPPRESS_GROUPS = Object.freeze({ NAV: "nav" });

/** ux/01 §4.2 — the back-office verbs (same key, same meaning everywhere). */
export const BACKOFFICE_VERB_DEFS = Object.freeze({
  RECHERCHER: K("RECHERCHER", "F2", "Rechercher", "Général"),
  NOUVEAU: K("NOUVEAU", "F3", "Nouveau", "Document", { commit: true, icon: "plus" }),
  TIERS: K("TIERS", "F4", "Choisir le tiers", "Document"),
  VALIDER: K("VALIDER", "F9", "Valider", "Document", {
    commit: true,
    bar: true,
    primary: true,
    icon: "check",
  }),
  ENREGISTRER: K("ENREGISTRER", "Ctrl+S", "Enregistrer", "Document", {
    commit: true,
    bar: true,
    icon: "save",
  }),
  IMPRIMER: K("IMPRIMER", "Ctrl+P", "Imprimer", "Document", { icon: "printer" }),
  SUPPRIMER: K("SUPPRIMER", "Delete", "Supprimer la ligne sélectionnée", "Lignes", {
    danger: true,
  }),
  FERMER: K("FERMER", "Escape", "Fermer", "Général"),
  VALIDER_SAISIE: K("VALIDER_SAISIE", "Ctrl+Enter", "Valider la saisie", "Lignes"),
  PRECEDENT: K("PRECEDENT", "Ctrl+ArrowUp", "Enregistrement précédent", "Navigation"),
  SUIVANT: K("SUIVANT", "Ctrl+ArrowDown", "Enregistrement suivant", "Navigation"),
});

/** The verb → display key table of ux/01 §4.4 (read by docs and chips). */
export const BACKOFFICE_VERBS = Object.freeze(
  Object.fromEntries(Object.entries(BACKOFFICE_VERB_DEFS).map(([id, d]) => [id, d.key])),
);

/** 02_UX_DESIGN §2.3 — screen-level keys that keep ONE meaning wherever the action exists. */
export const SHARED_ACTION_DEFS = Object.freeze({
  ETIQUETTES: K("ETIQUETTES", "Alt+I", "Imprimer les étiquettes", "Document", { icon: "tag" }),
  MODIFIER: K("MODIFIER", "Alt+O", "Modifier", "Document", { icon: "pencil" }),
  LIGNES: K("LIGNES", "Alt+L", "Aller au tableau des lignes", "Lignes"),
  EXPORTER: K("EXPORTER", "Alt+X", "Exporter", "Document", { icon: "download" }),
});

/**
 * 02_UX_DESIGN §2.5 — shared-action letters another module reuses for a
 * different meaning in a scope that never meets the shared action.
 * Key: the shared-action id; value: screen ids allowed to reuse its key.
 */
export const SHARED_KEY_REUSE = Object.freeze({
  ETIQUETTES: Object.freeze(["BORDEREAU_CHIFA_IMPORT"]),
  MODIFIER: Object.freeze(["BORDEREAU_CHIFA_IMPORT"]),
});

/**
 * ux/01 §4.2 « Reserved: never bind » (the browser owns them, or muscle
 * memory does). POS_SHORTCUTS predates the rule (F6) and is exempt.
 */
export const RESERVED_COMBOS = Object.freeze(
  [
    "Ctrl+N",
    "Ctrl+T",
    "Ctrl+W",
    "Ctrl+Tab",
    "F5",
    "Ctrl+R",
    "F6",
    "F10",
    "F11",
    "F12",
    "Alt+D",
    "Alt+E",
    "Alt+F",
    "Ctrl+L",
    "Ctrl+F",
  ].map(comboId),
);

/** A back-office verb for a screen map (label may be specialised, never the key). */
export const verb = (id, overrides) => {
  const base = BACKOFFICE_VERB_DEFS[id];
  if (!base) throw new Error(`shortcuts: unknown back-office verb « ${id} »`);
  return withOverrides(base, overrides);
};

/** A §2.3 shared action for a screen map. */
export const sharedAction = (id, overrides) => {
  const base = SHARED_ACTION_DEFS[id];
  if (!base) throw new Error(`shortcuts: unknown shared action « ${id} »`);
  return withOverrides(base, overrides);
};

/** A screen-specific key (F7, F8 or a free Alt+letter — 02_UX_DESIGN §2.6). */
export const screenKey = (id, combos, label, group, opts) => K(id, combos, label, group, opts);

/**
 * A screen map: its title (F1 sheet heading) and its bindings.
 * @param {string} title
 * @param {ReadonlyArray<KeyBinding>} entries
 */
export const screenShortcuts = (title, entries) =>
  Object.freeze({ title, entries: Object.freeze([...entries]) });

/**
 * One entry per screen map named in 02_UX_DESIGN §2.4 (ACHAT_SAISIE,
 * ACHAT_FICHE, PRODUITS, STOCK, BORDEREAUX_CHIFA…). Each map is added by the
 * module WP that migrates its screen, in the same commit as the handlers
 * (`useShortcuts(id, handlers)`), never ahead of them.
 * @type {Readonly<Record<string, {title: string, entries: ReadonlyArray<KeyBinding>}>>}
 */
export const SCREEN_SHORTCUTS = Object.freeze({
  // Refonte WP-22 — S1 « Nouvel achat » (design/ux/02_achats.md §3.1). The keys of the entry band's
  // own widgets (Entrée chain, Échap, Alt+Entrée in the product list, ↑ ↓ / Entrée / Suppr in the
  // lines grid) belong to those widgets and are shown on their controls; Alt+B « Bons de commande »
  // (WP-33, ux/03 §3.6) opens the fournisseur's open BCs; Alt+R « Réclamations » (WP-35, ux/03 §5.2.1)
  // opens the fournisseur's réclamations drawer — the facture being typed stays on screen. Refonte
  // WP-64 (D-26, CP §3.1-9) — « Supprimer le brouillon » is a visible danger button of the bar, first
  // and far from F9, on Alt+Suppr (the precedent of « Supprimer l'instance »; Suppr alone stays the
  // lines grid's own key); only a saved brouillon has it.
  ACHAT_SAISIE: screenShortcuts("Nouvel achat", [
    screenKey("SUPPRIMER_BROUILLON", "Alt+Delete", "Supprimer le brouillon", "Document", {
      bar: true,
      danger: true,
      icon: "trash-2",
      shortLabel: "Supprimer",
    }),
    verb("RECHERCHER", { label: "Aller à la recherche produit", shortLabel: "Produit", group: "Saisie", bar: true, icon: "search" }),
    verb("TIERS", { label: "Fournisseur et en-tête de la facture", shortLabel: "En-tête", group: "En-tête", bar: true, icon: "panel-left-open" }),
    screenKey("DETAIL_TOTAUX", "F7", "Détail des totaux et remise globale", "Totaux"),
    screenKey("REMPLIR_TOTAUX", "F8", "Remplir les totaux de la facture fournisseur", "Totaux", {
      bar: true,
      shortLabel: "Totaux",
      icon: "list-checks",
    }),
    verb("VALIDER_SAISIE", { label: "Ajouter / mettre à jour la ligne depuis n'importe quel champ", group: "Saisie" }),
    // Refonte WP-66 (D-29, CP §15.5) — the selector next to T.R: the line's own magasin / emplacement
    screenKey("DESTINATION_LIGNE", "Alt+N", "Magasin et emplacement de la ligne", "Saisie", { icon: "warehouse" }),
    sharedAction("LIGNES"),
    screenKey("SECOURS", "Alt+S", "Secours", "Fournisseur"),
    screenKey("MANQUANTS", "Alt+U", "Manquants", "Fournisseur"),
    screenKey("BONS_COMMANDE", "Alt+B", "Bons de commande en cours du fournisseur", "Fournisseur", { icon: "clipboard-list" }),
    screenKey("RECLAMATIONS", "Alt+R", "Réclamations du fournisseur", "Fournisseur", { icon: "message-square-warning" }),
    // X XWP-74 — the facture stays open; the line's product (else the whole stock) in a new window.
    stockNouvelleFenetre({ bar: true }),
    verb("ENREGISTRER", { label: "Enregistrer le brouillon", shortLabel: "Enregistrer" }),
    verb("VALIDER", { label: "Valider la facture", shortLabel: "Valider" }),
  ]),
  // Refonte WP-25 — S3 « Facture d'achat » /achats/:id (design/ux/02_achats.md §3.3, §6.9). Read
  // mode: Alt+O « Modifier la facture » (brouillon → S1, validée → edit mode), Alt+G « Régler »,
  // Alt+L the lines. Edit mode adds Ctrl+S / Échap and the S1 entry keys (F2, Ctrl+Entrée, F8).
  // Alt+I « Étiquettes » (WP-26: every lot; with the lines table focused, the active line's lot),
  // Alt+A / Alt+Q (avoir buttons, WP-29) and Alt+R « Réclamations du fournisseur » (WP-35: the drawer of
  // ux/03 §5.2.1; a line's « Signaler une réclamation » sits in the lines table's row menu).
  ACHAT_FICHE: screenShortcuts("Facture d'achat", [
    sharedAction("MODIFIER", { label: "Modifier la facture", shortLabel: "Modifier", bar: true }),
    sharedAction("ETIQUETTES", { label: "Étiquettes des lots", shortLabel: "Étiquettes", bar: true }),
    screenKey("REGLER", "Alt+G", "Régler la facture", "Document", { bar: true, shortLabel: "Régler", icon: "banknote" }),
    // WP-29 (ux/02 §6.8): « Créer / Voir l'avoir financier » and « Créer / Voir l'avoir produits »
    screenKey("AVOIR_FINANCIER", "Alt+A", "Créer / voir l'avoir financier (écart)", "Avoirs"),
    screenKey("AVOIR_PRODUITS", "Alt+Q", "Créer / voir l'avoir produits", "Avoirs"),
    // WP-35 (ux/02 §6.4, ux/03 §5.2.1): the fournisseur's réclamations drawer, « Réclamations · n »
    screenKey("RECLAMATIONS", "Alt+R", "Réclamations du fournisseur", "Réclamations", {
      bar: true,
      shortLabel: "Réclamations",
      icon: "message-square-warning",
    }),
    sharedAction("LIGNES"),
    verb("RECHERCHER", { label: "Aller à la recherche produit (mode modification)", shortLabel: "Produit", group: "Saisie" }),
    verb("VALIDER_SAISIE", { label: "Ajouter / mettre à jour la ligne depuis n'importe quel champ", group: "Saisie" }),
    // Refonte WP-66 (D-29) — edit mode: the magasin / emplacement of a line this edit adds
    screenKey("DESTINATION_LIGNE", "Alt+N", "Magasin et emplacement de la ligne ajoutée", "Saisie", { icon: "warehouse" }),
    screenKey("REMPLIR_TOTAUX", "F8", "Remplir les totaux de la facture fournisseur", "Totaux"),
    verb("ENREGISTRER", { label: "Enregistrer les modifications", shortLabel: "Enregistrer" }),
    verb("FERMER", { label: "Annuler les modifications" }),
  ]),
  // Cross-module X XWP-13 (refonte WP-38) — « Liste des produits » /produits (ux/04 §1.1.4). The
  // grid's own keys (↑ ↓ PgUp PgDn Début Fin, Entrée = open the panel) belong to the DataGrid;
  // Échap clears the search when no panel is open.
  PRODUITS: screenShortcuts("Liste des produits", [
    verb("RECHERCHER", { label: "Aller à la recherche", shortLabel: "Rechercher" }),
    verb("NOUVEAU", { label: "Nouveau produit", shortLabel: "Nouveau" }),
    sharedAction("MODIFIER", { label: "Modifier la fiche du produit sélectionné", shortLabel: "Modifier" }),
    sharedAction("EXPORTER", { label: "Exporter la liste filtrée (CSV)", shortLabel: "Exporter" }),
  ]),
  // Cross-module X XWP-15 (refonte WP-40) — « Stock & lots » /stock (ux/04 §2.1). The grid's own keys
  // (↑ ↓ PgUp PgDn Début Fin, Entrée = the product panel on that lot, Maj+F10 = the row menu) belong
  // to the DataGrid; ←/→ walk the views strip; Alt+C / Alt+I act on the focused lot row.
  STOCK_LOTS: screenShortcuts("Stock & lots", [
    verb("RECHERCHER", { label: "Rechercher un produit, un N° lot ou un code lot", shortLabel: "Rechercher" }),
    screenKey("CORRIGER_LOT", "Alt+C", "Corriger le stock du lot sélectionné", "Lots", { icon: "sliders-horizontal" }),
    sharedAction("ETIQUETTES", { label: "Imprimer l'étiquette du lot sélectionné", shortLabel: "Étiquette" }),
    sharedAction("EXPORTER", { label: "Exporter la vue affichée (CSV)", shortLabel: "Exporter" }),
    verb("IMPRIMER", { label: "Imprimer la vue affichée" }),
  ]),
  // X XWP-16 — the /magasins workspace (02_DESIGN §3.2), one map per view. The tree's own keys
  // (↑ ↓ → ← Début Fin, Entrée = show the node) and the grids' (Entrée = open, Maj+F10 = the row
  // menu, Suppr = delete) belong to those widgets; Alt+R / Alt+T / Alt+C act on the lot row that
  // holds the focus.
  MAGASINS_ENSEMBLE: screenShortcuts("Magasins — vue d'ensemble", [
    verb("RECHERCHER", { label: "Rechercher un lot du nœud affiché", shortLabel: "Rechercher" }),
    verb("NOUVEAU", { label: "Nouvel emplacement dans le magasin affiché (sinon nouveau magasin)", shortLabel: "Nouveau" }),
    screenKey("REPARTIR", "Alt+R", "Répartir le lot sélectionné entre les magasins", "Lots", { icon: "boxes" }),
    screenKey("TRANSFERER", "Alt+T", "Transférer le lot sélectionné vers un autre magasin", "Lots", { icon: "arrow-left-right" }),
    screenKey("CORRIGER_LOT", "Alt+C", "Corriger le stock du lot sélectionné", "Lots", { icon: "sliders-horizontal" }),
    sharedAction("MODIFIER", { label: "Modifier le magasin ou l'emplacement affiché", shortLabel: "Modifier" }),
  ]),
  MAGASINS_EMPLACEMENTS: screenShortcuts("Magasins — emplacements", [
    verb("RECHERCHER", { label: "Rechercher un emplacement", shortLabel: "Rechercher" }),
    verb("NOUVEAU", { label: "Nouvel emplacement", shortLabel: "Nouveau" }),
    sharedAction("MODIFIER", { label: "Modifier l'emplacement sélectionné", shortLabel: "Modifier" }),
  ]),
  MAGASINS_TRANSFERTS: screenShortcuts("Magasins — transferts", [
    verb("RECHERCHER", { label: "Rechercher un N° de transfert", shortLabel: "Rechercher" }),
    verb("NOUVEAU", { label: "Nouveau transfert", shortLabel: "Nouveau" }),
  ]),
  // X XWP-16 — « Nouveau transfert entre magasins »: F9 transfers now (the dialog's primary),
  // Ctrl+S keeps the draft for later validation.
  MAGASINS_TRANSFERT_DIALOGUE: screenShortcuts("Nouveau transfert", [
    verb("ENREGISTRER", { label: "Enregistrer le brouillon", shortLabel: "Brouillon" }),
  ]),
  // X XWP-15 — « Corrections de stock » /stock/corrections, the journal (ux/04 §2.3).
  STOCK_CORRECTIONS: screenShortcuts("Corrections de stock", [
    verb("RECHERCHER", { label: "Rechercher un produit, un N° lot ou un code lot", shortLabel: "Rechercher" }),
    verb("IMPRIMER", { label: "Imprimer le journal affiché" }),
  ]),
  // X XWP-15 — « Risque de péremption » /stock/risque-peremption (ux/04 §2.5).
  STOCK_RISQUE: screenShortcuts("Risque de péremption", [
    sharedAction("EXPORTER", { label: "Exporter le classement (CSV)", shortLabel: "Exporter" }),
    verb("IMPRIMER", { label: "Imprimer le classement" }),
  ]),
  // X XWP-13 — the product panel (overlay map, ux/04 §1.2-§1.2.3, X 02_DESIGN §3.4): the drawer
  // walks the list with Ctrl+↑ / Ctrl+↓; Alt+C acts on the lot card holding the focus (else the
  // first expanded one).
  PRODUIT_PANNEAU: screenShortcuts("Panneau produit", [
    sharedAction("MODIFIER", { label: "Modifier la fiche", shortLabel: "Modifier" }),
    screenKey("GENERIQUES", "Alt+G", "Génériques", "Document", { icon: "pill" }),
    sharedAction("ETIQUETTES", { label: "Imprimer les étiquettes des lots vendables", shortLabel: "Étiquettes" }),
    screenKey("COMPARER_PRIX", "Alt+K", "Comparer les prix d'achat", "Document", { icon: "bar-chart-3" }),
    screenKey("BLOQUER_QUANTITE", "Alt+B", "Bloquer une quantité", "Lots", { icon: "lock" }),
    screenKey("CORRIGER_LOT", "Alt+C", "Corriger le stock du lot", "Lots", { icon: "sliders-horizontal" }),
    // X XWP-97 (D-49) — « Invisible aux caissiers » of the focused lot card or reception (P only)
    screenKey("VISIBILITE_LOT", "Alt+V", "Invisible aux caissiers (lot sélectionné)", "Lots", { icon: "eye-off" }),
    verb("PRECEDENT", { label: "Produit précédent de la liste" }),
    verb("SUIVANT", { label: "Produit suivant de la liste" }),
    verb("FERMER", { label: "Fermer le panneau" }),
  ]),
  // X XWP-13 — « Génériques » (ux/04 §1.3) and « Comparer les prix d'achat » (02_DESIGN §3.4).
  PRODUIT_GENERIQUES: screenShortcuts("Génériques", [
    verb("IMPRIMER", { label: "Imprimer la comparaison" }),
    verb("FERMER", { label: "Fermer" }),
  ]),
  PRODUIT_COMPARER_PRIX: screenShortcuts("Comparer les prix d'achat", [
    screenKey("AFFICHER_GENERIQUES", "Alt+G", "Afficher / masquer les génériques", "Document"),
    screenKey("AFFICHER_TABLEAU", "Alt+T", "Afficher / masquer le tableau", "Document"),
    verb("FERMER", { label: "Fermer" }),
  ]),
  // X XWP-14 (refonte WP-39) — the fiche produit /produits/:id, /produits/nouveau (ux/04 §1.4.1):
  // Ctrl+S saves from anywhere, Échap = Annuler (dirty guard), Alt+Pg préc. / Pg suiv. walk the
  // sections (Alt+1…9 belong to the menu). Entrée in a single-line field moves to the next field
  // (the form's own behaviour, said in the section index, not a registry key).
  PRODUIT_FICHE: screenShortcuts("Fiche produit", [
    verb("FERMER", { label: "Annuler et revenir", shortLabel: "Annuler", bar: true, icon: "x" }),
    verb("ENREGISTRER", { label: "Enregistrer la fiche", shortLabel: "Enregistrer", primary: true }),
    screenKey("SECTION_PRECEDENTE", "Alt+PageUp", "Section précédente", "Navigation"),
    screenKey("SECTION_SUIVANTE", "Alt+PageDown", "Section suivante", "Navigation"),
  ]),
  // Refonte WP-29 — « Nouvel avoir fournisseur » /avoirs/nouveau (design/ux/03 §0.5, §1.13). F3 adds
  // a Retour line in the editor and starts a new avoir once saved; Alt+O re-opens the factures
  // d'origine (the shared « Modifier » key); Entrée / Espace belong to the factures list and the
  // lines grid (shown on their controls); Ctrl+P prints once saved.
  ACHATS_AVOIR_EDITEUR: screenShortcuts("Nouvel avoir fournisseur", [
    verb("RECHERCHER", { label: "Rechercher une facture (ou la ligne de facture de la nouvelle ligne)", shortLabel: "Rechercher", bar: true, icon: "search" }),
    verb("TIERS", { label: "Fournisseur et en-tête de l'avoir", shortLabel: "Fournisseur", group: "En-tête", bar: true, icon: "panel-left-open" }),
    sharedAction("MODIFIER", { label: "Modifier les factures d'origine", shortLabel: "Factures", group: "Factures", bar: true }),
    // refonte WP-86 (CP §5.2) — a line of any invoice line, any motif (the Manquant / Retour natures and the
    // « computed quantity » of WP-29 are gone: nothing to restore, Ctrl+Retour arrière retired)
    verb("NOUVEAU", { label: "Ajouter une ligne d'avoir / nouvel avoir", shortLabel: "Ligne", group: "Lignes", icon: "plus", bar: true }),
    sharedAction("LIGNES", { label: "Aller aux lignes de l'avoir" }),
    // refonte WP-87 (CP §5.4) — goods received without an invoice line: no facture needed
    screenKey("RECU_NON_FACTURE", "Alt+N", "Saisir un reçu non facturé (marchandise reçue sans facture)", "Lignes", { icon: "package-plus", shortLabel: "Reçu non facturé" }),
    verb("SUPPRIMER", { label: "Retirer la ligne de l'avoir" }),
    // X XWP-74 — the avoir stays open; the active line's product (else the whole stock) in a new window.
    stockNouvelleFenetre({ bar: true }),
    verb("IMPRIMER", { label: "Imprimer l'avoir (après enregistrement)" }),
    verb("FERMER", { label: "Fermer" }),
    verb("VALIDER", { label: "Enregistrer l'avoir", shortLabel: "Enregistrer", icon: "save" }),
  ]),
  // Refonte WP-30 — « Journal des avoirs fournisseur » /avoirs (design/ux/03 §0.5, §2.8). F2 the
  // search, F4 the Fournisseur filter, F3 « Nouvel avoir », Alt+X the CSV export of the filter,
  // Ctrl+P the filtered journal printed A4 landscape; ↑ ↓ / Entrée are the grid's own keys (Entrée
  // opens the drawer), Suppr on the active row opens « Annuler l'avoir… » only where allowed.
  ACHATS_AVOIR_JOURNAL: screenShortcuts("Journal des avoirs fournisseur", [
    verb("RECHERCHER", { label: "Rechercher dans le journal", group: "Journal" }),
    verb("TIERS", { label: "Filtrer par fournisseur", shortLabel: "Fournisseur", group: "Journal" }),
    verb("NOUVEAU", { label: "Nouvel avoir", shortLabel: "Nouvel avoir", bar: true }),
    verb("SUPPRIMER", { label: "Annuler l'avoir sélectionné", group: "Journal" }),
    sharedAction("EXPORTER", { label: "Exporter le journal filtré (CSV)", shortLabel: "Exporter", bar: true }),
    verb("IMPRIMER", { label: "Imprimer le journal (A4 paysage)", shortLabel: "Imprimer", bar: true }),
  ]),
  // WP-30 — the avoir drawer /avoirs/:id (overlay map, ux/03 §2.5): F9 the règlement, Ctrl+P the avoir
  // (A4 document), Ctrl+↑ / Ctrl+↓ the previous / next avoir of the journal without closing.
  // refonte WP-83 (D-32) — F9 confirms an avoir en instance, else records its règlement
  ACHATS_AVOIR_TIROIR: screenShortcuts("Avoir fournisseur", [
    verb("VALIDER", { label: "Confirmer l'avoir (en instance) ou enregistrer le règlement", shortLabel: "Confirmer / régler", icon: "file-check-2" }),
    verb("IMPRIMER", { label: "Imprimer l'avoir (A4)" }),
    verb("PRECEDENT", { label: "Avoir précédent du journal" }),
    verb("SUIVANT", { label: "Avoir suivant du journal" }),
    verb("FERMER", { label: "Fermer l'avoir" }),
  ]),
  // X XWP-20 — « Crédits clients » /credits (02_DESIGN §4.3): F2 the search, Alt+X the CSV of the
  // filter; ↑ ↓ / Entrée are the grid's own keys (Entrée opens the crédit's drawer).
  CREDIT_JOURNAL: screenShortcuts("Crédits clients", [
    verb("RECHERCHER", { label: "Rechercher un client ou un N° de crédit", group: "Journal" }),
    sharedAction("EXPORTER", { label: "Exporter le journal filtré (CSV)", shortLabel: "Exporter", bar: true }),
  ]),
  // XWP-20 — the crédit drawer /credits/:id (overlay map, §6.4): F9 « Verser un montant », Alt+P
  // « Marquer payé » (= verser le solde restant, §6.2), Ctrl+↑ / Ctrl+↓ the previous / next crédit.
  CREDIT_TIROIR: screenShortcuts("Crédit client", [
    verb("VALIDER", { label: "Verser un montant", shortLabel: "Verser", icon: "banknote" }),
    screenKey("MARQUER_PAYE", "Alt+P", "Marquer payé (verser le solde restant)", "Document"),
    verb("PRECEDENT", { label: "Crédit précédent du journal" }),
    verb("SUIVANT", { label: "Crédit suivant du journal" }),
    verb("FERMER", { label: "Fermer le crédit" }),
  ]),
  // X XWP-23 — « Clients » /clients (§4.4, §3.2): F2 the search, F3 « Nouveau client »; ↑ ↓ / Entrée
  // are the grid's own keys (Entrée opens the client's fiche).
  CLIENTS: screenShortcuts("Clients", [
    verb("RECHERCHER", { label: "Rechercher un client (nom, téléphone, CIN, carte CHIFA)", group: "Répertoire" }),
    verb("NOUVEAU", { label: "Nouveau client", shortLabel: "Nouveau client", bar: true, icon: "user-plus" }),
  ]),
  // X XWP-23 — the client form /clients/nouveau, /clients/:id/edit: Ctrl+S « Enregistrer le client »
  // (Entrée in a one-line field submits too).
  CLIENT_FORM: screenShortcuts("Fiche client", [
    verb("ENREGISTRER", { label: "Enregistrer le client", shortLabel: "Enregistrer", primary: true }),
  ]),
  // X XWP-68 — the one team workspace /equipe (CP §15.3, XMD-CP-20): F2 the search, F3 « Ajouter un
  // utilisateur » (the owner); ↑ ↓ / Entrée are the grid's own keys (Entrée opens the user's sidebar).
  EQUIPE: screenShortcuts("Équipe & utilisateurs", [
    verb("RECHERCHER", { label: "Rechercher un utilisateur (nom, e-mail, téléphone)", group: "Équipe" }),
    verb("NOUVEAU", { label: "Ajouter un utilisateur", shortLabel: "Ajouter un utilisateur", bar: true, primary: true, icon: "user-plus" }),
  ]),
  // X XWP-68 — the same workspace as Paramètres › Utilisateurs, registered by the Paramètres page while
  // the section is active (`usePublicationSection` `ecran`, TA-45): it keeps the workspace's section keys.
  EQUIPE_PARAMETRES: screenShortcuts("Paramètres › Utilisateurs", [
    verb("RECHERCHER", { label: "Rechercher un utilisateur (nom, e-mail, téléphone)", group: "Équipe" }),
    verb("NOUVEAU", { label: "Ajouter un utilisateur", shortLabel: "Ajouter un utilisateur", bar: true, primary: true, icon: "user-plus" }),
    screenKey("SECTION_PRECEDENTE", "Alt+PageUp", "Section précédente", "Navigation"),
    screenKey("SECTION_SUIVANTE", "Alt+PageDown", "Section suivante", "Navigation"),
  ]),
  // X XWP-68 — the user sidebar (overlay map): Alt+O « Modifier » (the owner), Ctrl+↑ / Ctrl+↓ the
  // previous / next user of the list; ← / → change the tab (the Tabs primitive's own keys).
  EQUIPE_TIROIR: screenShortcuts("Fiche utilisateur", [
    sharedAction("MODIFIER", { label: "Modifier l'utilisateur", shortLabel: "Modifier" }),
    verb("PRECEDENT", { label: "Utilisateur précédent de la liste" }),
    verb("SUIVANT", { label: "Utilisateur suivant de la liste" }),
    verb("FERMER", { label: "Fermer la fiche" }),
  ]),
  // X XWP-21 — « Instances CHIFA » /instances-chifa (02_DESIGN §4.4): F2 the search, F3 « Nouvelle
  // instance »; ↑ ↓ / Entrée are the grid's own keys (Entrée opens the instance's drawer).
  INSTANCES_CHIFA_JOURNAL: screenShortcuts("Instances CHIFA", [
    verb("RECHERCHER", { label: "Rechercher un client, un N° d'instance ou de facture CHIFA", group: "Journal" }),
    verb("NOUVEAU", { label: "Nouvelle instance", shortLabel: "Nouvelle instance", bar: true }),
  ]),
  // XWP-21 — the instance drawer /instances-chifa/:id (overlay map, §7): on the active line of the
  // « Lignes » grid Alt+O « Modifier la quantité », F7 « Changer le lot », Suppr « Retirer la ligne »
  // (P / M, PENDING, motif); F8 « Envoyer vers CHIFA », F9 « Régler en CHIFA », Alt+Suppr « Supprimer
  // l'instance » (P / M); Ctrl+↑ / Ctrl+↓ the previous / next instance of the journal.
  INSTANCE_CHIFA_TIROIR: screenShortcuts("Instance CHIFA", [
    sharedAction("MODIFIER", { label: "Modifier la quantité de la ligne sélectionnée", group: "Lignes" }),
    screenKey("CHANGER_LOT", "F7", "Changer le lot de la ligne sélectionnée", "Lignes"),
    verb("SUPPRIMER", { label: "Retirer la ligne sélectionnée" }),
    screenKey("ENVOYER_CHIFA", "F8", "Envoyer vers CHIFA Officine", "Document", { icon: "send" }),
    verb("VALIDER", { label: "Régler en CHIFA", shortLabel: "Régler", icon: "check" }),
    screenKey("SUPPRIMER_INSTANCE", "Alt+Delete", "Supprimer l'instance", "Document", { danger: true }),
    // X XWP-22 (§8.1) — from the caisse only (the drawer opened by « Instances du client »)
    screenKey("VENTE_LIBRE", "Alt+V", "Envoyer vente libre (dans la fenêtre de caisse)", "Document", { icon: "shopping-cart" }),
    verb("PRECEDENT", { label: "Instance précédente du journal" }),
    verb("SUIVANT", { label: "Instance suivante du journal" }),
    verb("FERMER", { label: "Fermer l'instance" }),
  ]),
  // X XWP-55 — « Ordonnancier » /ordonnancier (CP §15.14; XMD-CP-11): F2 the N° d'ordonnance search,
  // Ctrl+P the register printed; ↑ ↓ / Entrée are the grid's own keys (Entrée opens the delivery's drawer).
  ORDONNANCIER_JOURNAL: screenShortcuts("Ordonnancier", [
    verb("RECHERCHER", { label: "Rechercher un N° d'ordonnance", group: "Journal" }),
    verb("IMPRIMER", { label: "Imprimer le registre", shortLabel: "Imprimer", bar: true }),
  ]),
  // X XWP-55 — the delivery drawer /ordonnancier/:id (overlay map): Alt+O « Compléter / corriger la
  // délivrance » (P / M, not when signed), Alt+J « Joindre une pièce » (the type, then the file chooser;
  // P / M), Ctrl+↑ / Ctrl+↓ the previous / next delivery of the register, Échap closes.
  ORDONNANCIER_TIROIR: screenShortcuts("Délivrance (ordonnancier)", [
    sharedAction("MODIFIER", { label: "Compléter / corriger la délivrance", shortLabel: "Délivrance" }),
    screenKey("JOINDRE", "Alt+J", "Joindre une pièce (pièce d'identité, ordonnance)", "Document", { icon: "paperclip" }),
    verb("PRECEDENT", { label: "Délivrance précédente du registre" }),
    verb("SUIVANT", { label: "Délivrance suivante du registre" }),
    verb("FERMER", { label: "Fermer la délivrance" }),
  ]),
  // Refonte WP-31 — « Journal des bons de retour » /achats/bons-retour (design/ux/03 §0.5, §4.2). F2 the
  // search, F4 the Fournisseur filter, F3 « Nouveau bon de retour », Alt+X the CSV of the filter,
  // Ctrl+P the filtered journal A4 landscape (§6.6); ↑ ↓ / Entrée are the grid's own keys (Entrée
  // opens the document), Suppr on the active row: a brouillon is deleted, a validated one cancelled
  // (motif) — always through a dialog, only where allowed.
  ACHATS_BR_JOURNAL: screenShortcuts("Journal des bons de retour", [
    verb("RECHERCHER", { label: "Rechercher dans le journal", group: "Journal" }),
    verb("TIERS", { label: "Filtrer par fournisseur", shortLabel: "Fournisseur", group: "Journal" }),
    verb("NOUVEAU", { label: "Nouveau bon de retour", shortLabel: "Nouveau", bar: true }),
    verb("SUPPRIMER", { label: "Supprimer le brouillon / annuler le bon de retour sélectionné", group: "Journal" }),
    sharedAction("EXPORTER", { label: "Exporter le journal filtré (CSV)", shortLabel: "Exporter", bar: true }),
    verb("IMPRIMER", { label: "Imprimer le journal (A4 paysage)", shortLabel: "Imprimer", bar: true }),
  ]),
  // WP-31 — « Nouveau bon de retour » /achats/bons-retour/nouveau and a brouillon /achats/bons-retour/:id
  // (ux/03 §4.3 map ACHATS_BR_EDITEUR): F2 the product (or facture) search of the entry, F4 the
  // fournisseur, F3 adds the line being typed, Suppr removes the active line, Ctrl+S saves the
  // brouillon, F9 validates (confirmation: the units leave the stock), Ctrl+P prints once validated.
  // The bar keeps the design's one row at 1366 (§4.3 « F2 Produit · F4 Fournisseur · F3 Ajouter ·
  // Ctrl+S Brouillon · F9 Valider »): F2 is shown on the Produit / Facture field and in the F1 sheet.
  ACHATS_BR_EDITEUR: screenShortcuts("Bon de retour", [
    verb("RECHERCHER", { label: "Rechercher le produit (ou la facture)", shortLabel: "Produit", icon: "search" }),
    verb("TIERS", { label: "Fournisseur et en-tête", shortLabel: "Fournisseur", group: "En-tête", bar: true, icon: "panel-left-open" }),
    verb("NOUVEAU", { label: "Ajouter la ligne", shortLabel: "Ajouter", group: "Lignes", bar: true }),
    sharedAction("LIGNES", { label: "Aller aux lignes du retour" }),
    verb("SUPPRIMER", { label: "Retirer la ligne" }),
    verb("IMPRIMER", { label: "Imprimer le bon de retour (après validation)" }),
    verb("FERMER", { label: "Fermer" }),
    verb("ENREGISTRER", { label: "Enregistrer le brouillon", shortLabel: "Brouillon" }),
    verb("VALIDER", { label: "Valider", shortLabel: "Valider" }),
  ]),
  // WP-31 — the bon de retour document /achats/bons-retour/:id once validated (ux/03 §4.3 « document
  // page », §0.5 detail pages): F9 its primary action « Créer l'avoir produits » (units still to
  // credit), Ctrl+P the A4 document, Suppr « Annuler le bon de retour… » where allowed, Échap back.
  ACHATS_BR_DOCUMENT: screenShortcuts("Bon de retour fournisseur", [
    verb("IMPRIMER", { label: "Imprimer le bon de retour (A4)", shortLabel: "Imprimer", bar: true }),
    verb("SUPPRIMER", { label: "Annuler le bon de retour…", group: "Document" }),
    verb("FERMER", { label: "Retour au journal" }),
    verb("VALIDER", { label: "Créer l'avoir produits", shortLabel: "Avoir produits", icon: "receipt" }),
  ]),
  // Refonte WP-35 — « Journal des réclamations » /achats/reclamations (design/ux/03 §5.4, §0.5). F2 the
  // search, F4 the Fournisseur filter, F3 « Nouvelle réclamation », Ctrl+P the filtered journal A4
  // landscape (§6.6); ↑ ↓ / Entrée are the grid's own keys (Entrée opens the réclamation), Suppr on the
  // active row deletes a brouillon without number — through its dialog, only where allowed.
  ACHATS_RECL_JOURNAL: screenShortcuts("Journal des réclamations", [
    verb("RECHERCHER", { label: "Rechercher dans le journal", group: "Journal" }),
    verb("TIERS", { label: "Filtrer par fournisseur", shortLabel: "Fournisseur", group: "Journal" }),
    verb("NOUVEAU", { label: "Nouvelle réclamation", shortLabel: "Nouvelle", bar: true }),
    verb("SUPPRIMER", { label: "Supprimer le brouillon sélectionné", group: "Journal" }),
    verb("IMPRIMER", { label: "Imprimer le journal (A4 paysage)", shortLabel: "Imprimer", bar: true }),
  ]),
  // WP-35 — the réclamation composer (ux/03 §5.3: the drawer of S1 / S3 / the journal, or the full page
  // /achats/reclamations/nouvelle and a brouillon): F4 the fournisseur, F3 « Lignes de la facture »,
  // Ctrl+S the brouillon, Ctrl+P the A4 letter, F9 « Envoyer » (ConfirmDialog; the e-mail dialog or the
  // print for a non-connected fournisseur), Échap closes (unsaved-changes guard).
  ACHATS_RECL_COMPOSEUR: screenShortcuts("Réclamation", [
    verb("TIERS", { label: "Choisir le fournisseur", shortLabel: "Fournisseur", group: "Réclamation" }),
    verb("NOUVEAU", { label: "Lignes de la facture", shortLabel: "Lignes", group: "Lignes", icon: "list-plus" }),
    verb("IMPRIMER", { label: "Imprimer la lettre de réclamation (A4)" }),
    verb("FERMER", { label: "Fermer" }),
    verb("ENREGISTRER", { label: "Enregistrer le brouillon", shortLabel: "Brouillon" }),
    verb("VALIDER", { label: "Envoyer la réclamation", shortLabel: "Envoyer", icon: "send" }),
  ]),
  // WP-35 — the réclamation document /achats/reclamations/:id once sent (ux/03 §5.5, §0.5 detail pages,
  // map ACHATS_RECL_DETAIL): F9 its primary action of the moment (« Accepter la proposition »,
  // « Générer l'avoir »), Ctrl+P the A4 letter, Ctrl+Entrée records the message / note of the right
  // column, Suppr « Retirer… » where allowed, Échap back to the journal (or the drawer's list).
  ACHATS_RECL_DETAIL: screenShortcuts("Réclamation fournisseur", [
    verb("IMPRIMER", { label: "Imprimer la lettre de réclamation (A4)", shortLabel: "Imprimer", bar: true }),
    verb("VALIDER_SAISIE", { label: "Enregistrer le message ou la note", group: "Échanges" }),
    verb("SUPPRIMER", { label: "Retirer la réclamation…", group: "Document" }),
    verb("FERMER", { label: "Retour au journal" }),
    // refonte WP-89 (K1 A5 (1)) — « Accepter la proposition » while it is offered, else « Créer l'avoir » (the editor)
    verb("VALIDER", { label: "Action principale (accepter la proposition, créer l'avoir)", shortLabel: "Valider", icon: "check" }),
  ]),
  // WP-35 — the §B2 « Réclamations — {fournisseur} » drawer of S1 / S3 (overlay map, ux/03 §5.2.1): the
  // list's own keys (↑ ↓, Entrée = open in the drawer); F3 « Nouvelle réclamation » in the same drawer.
  ACHATS_RECL_TIROIR: screenShortcuts("Réclamations du fournisseur", [
    verb("NOUVEAU", { label: "Nouvelle réclamation", shortLabel: "Nouvelle", bar: true }),
    verb("FERMER", { label: "Fermer le tiroir" }),
  ]),
  // WP-36 — the fournisseur portal « Réclamations reçues » (ux/03 §5.8 map PORTAIL_RECL): F2 the list's
  // search, Entrée opens the row (the grid's own key), F9 « Accepter… » on the document, Ctrl+Entrée sends
  // the message of « Échanges », Échap back to the list. Each page binds only its own keys.
  PORTAIL_RECL: screenShortcuts("Réclamations reçues", [
    verb("RECHERCHER", { label: "Rechercher dans les réclamations reçues", group: "Liste" }),
    verb("VALIDER_SAISIE", { label: "Envoyer le message à la pharmacie", group: "Échanges" }),
    verb("FERMER", { label: "Retour à la liste" }),
    verb("VALIDER", { label: "Accepter la réclamation…", shortLabel: "Accepter…", bar: true }),
  ]),
  // Refonte WP-33 — « Journal des bons de commande » /commercial/bons-commande (ux/03 §3.2, §0.5): F2 the
  // search, F4 the Fournisseur filter, F3 « Nouveau bon de commande », Suppr deletes the brouillon /
  // cancels the BC selected (only where allowed, through its dialog), Alt+X the CSV of the filter,
  // Ctrl+P the filtered journal (A4 landscape). → / ← unfold / fold the row's lines (the tree-grid's
  // own keys), Entrée opens the BC.
  ACHATS_BC_JOURNAL: screenShortcuts("Journal des bons de commande", [
    verb("RECHERCHER", { label: "Rechercher dans le journal", group: "Journal" }),
    verb("TIERS", { label: "Filtrer par fournisseur", shortLabel: "Fournisseur", group: "Journal" }),
    verb("NOUVEAU", { label: "Nouveau bon de commande", shortLabel: "Nouveau bon", bar: true }),
    verb("SUPPRIMER", { label: "Supprimer le brouillon / annuler le bon de commande sélectionné", group: "Journal" }),
    sharedAction("EXPORTER", { label: "Exporter le journal filtré (CSV)", shortLabel: "Exporter", bar: true }),
    verb("IMPRIMER", { label: "Imprimer le journal (A4 paysage)", shortLabel: "Imprimer", bar: true }),
  ]),
  // WP-33 — « Nouveau bon de commande » /commercial/bons-commande/nouveau and a brouillon (ux/03 §3.3.1,
  // §3.8): F2 the product of the entry, F4 the fournisseur, F3 adds the line being typed (Entrée in the
  // entry does too), Alt+L the lines, Suppr removes the active line, Ctrl+S saves the brouillon, F9
  // « Envoyer… » (the send dialog), Échap closes (unsaved-changes guard).
  ACHATS_BC_EDITEUR: screenShortcuts("Bon de commande", [
    verb("RECHERCHER", { label: "Aller au produit de la saisie", shortLabel: "Produit", icon: "search" }),
    verb("TIERS", { label: "Fournisseur et en-tête", shortLabel: "Fournisseur", group: "En-tête", bar: true, icon: "panel-left-open" }),
    verb("NOUVEAU", { label: "Ajouter la ligne", shortLabel: "Ajouter", group: "Lignes" }),
    sharedAction("LIGNES", { label: "Aller aux lignes du bon" }),
    verb("SUPPRIMER", { label: "Retirer la ligne" }),
    // X XWP-74 — the bon stays open; the active line's product (else the whole stock) in a new window.
    stockNouvelleFenetre({ bar: true }),
    verb("FERMER", { label: "Fermer" }),
    verb("ENREGISTRER", { label: "Enregistrer le brouillon", shortLabel: "Brouillon", bar: true }),
    verb("VALIDER", { label: "Envoyer le bon de commande…", shortLabel: "Envoyer…", icon: "send" }),
  ]),
  // WP-33 — the bon de commande document /commercial/bons-commande/:id once sent (ux/03 §3.4, §0.5
  // detail pages): F9 its primary action « Créer la facture » (Envoyé / Partiellement reçu), Ctrl+P
  // the A4 document, Suppr « Annuler le bon… » / « Annuler le reliquat… » where allowed, Échap back.
  ACHATS_BC_DOCUMENT: screenShortcuts("Bon de commande", [
    verb("IMPRIMER", { label: "Imprimer le bon de commande (A4)", shortLabel: "Imprimer", bar: true }),
    verb("SUPPRIMER", { label: "Annuler le bon / le reliquat…", group: "Document" }),
    verb("FERMER", { label: "Retour au journal" }),
    verb("VALIDER", { label: "Créer la facture", shortLabel: "Créer la facture", icon: "receipt" }),
  ]),
  // Refonte WP-52 — the purchase journals S4 « Journal des achats » (/achats) and S5 « Journal
  // détaillé des achats » (/achats/journal-detaille) (design/ux/02_achats.md §3.4). F2 the search,
  // F3 « Nouvel achat » (S4), Alt+X the CSV export, Ctrl+P the journal printed A4 landscape (S5);
  // ↑ ↓ / Entrée are the grid's own keys, Échap clears the search field (FilterBar). S6 (WP-53)
  // joins this map with its screen. Refonte WP-64 (D-26) — S4: Suppr on the active row asks to
  // delete that brouillon (the row's own « Supprimer le brouillon » button does the same); a
  // validated facture is never deleted.
  ACHAT_JOURNAUX: screenShortcuts("Journaux des achats", [
    verb("RECHERCHER", { label: "Rechercher dans le journal", group: "Journal" }),
    verb("NOUVEAU", { label: "Nouvel achat", shortLabel: "Nouvel achat", bar: true }),
    verb("SUPPRIMER", { label: "Supprimer le brouillon sélectionné", group: "Journal" }),
    sharedAction("EXPORTER", { label: "Exporter (CSV)", shortLabel: "Exporter", bar: true }),
    verb("IMPRIMER", { label: "Imprimer le journal (A4 paysage)", shortLabel: "Imprimer", bar: true }),
  ]),
  // Trésorerie TWP-18 — C1 « Comptes & caisses » /tresorerie/comptes[/sessions] (UX 02 §3, 03_UX §4.3).
  // The grid's keys (↑ ↓, → / ← déplier / replier, Entrée ouvrir, Maj+F10 row menu) belong to the
  // DataGrid. TWP-20 — F7 « Nouveau mouvement sur ce compte », Alt+T « Transférer » and Alt+V
  // « Affecter » open the one OperationForm on the selected account (TA-44: one meaning per key in
  // Trésorerie); Ctrl+P « Imprimer l'état des comptes » joins with its print document — never ahead
  // of its handler. Alt+X exports the sessions tab (CSV).
  TRESO_COMPTES: screenShortcuts("Comptes & caisses", [
    verb("RECHERCHER", { label: "Rechercher un compte", shortLabel: "Rechercher" }),
    verb("NOUVEAU", { label: "Nouveau… (compte bancaire, caisse physique, virtuelle, comptoir)", shortLabel: "Nouveau…" }),
    screenKey("NOUVEAU_MOUVEMENT", "F7", "Nouveau mouvement sur le compte sélectionné", "Mouvements", { icon: "arrow-left-right" }),
    screenKey("TRANSFERER", "Alt+T", "Transférer depuis le compte sélectionné", "Mouvements", { icon: "arrow-right-left" }),
    screenKey("AFFECTER", "Alt+V", "Affecter à une caisse virtuelle (caisse parente sélectionnée)", "Mouvements", { icon: "folder-input" }),
    sharedAction("EXPORTER", { label: "Exporter les sessions filtrées (CSV)", shortLabel: "Exporter" }),
    screenKey("ONGLET_PRECEDENT", "Alt+PageUp", "Onglet précédent", "Navigation"),
    screenKey("ONGLET_SUIVANT", "Alt+PageDown", "Onglet suivant", "Navigation"),
  ]),
  // C2 — the account drawer (overlay map, UX 02 §5): it walks the tree with Ctrl+↑ / Ctrl+↓; F7 /
  // Alt+T / Alt+V open the one OperationForm on this account (TWP-20).
  TRESO_COMPTE_DRAWER: screenShortcuts("Tiroir du compte", [
    sharedAction("MODIFIER", { label: "Modifier le compte", shortLabel: "Modifier" }),
    screenKey("NOUVEAU_MOUVEMENT", "F7", "Nouveau mouvement sur ce compte", "Mouvements", { icon: "arrow-left-right" }),
    screenKey("TRANSFERER", "Alt+T", "Transférer depuis ce compte", "Mouvements", { icon: "arrow-right-left" }),
    screenKey("AFFECTER", "Alt+V", "Affecter à une caisse virtuelle de ce compte", "Mouvements", { icon: "folder-input" }),
    verb("PRECEDENT", { label: "Compte précédent" }),
    verb("SUIVANT", { label: "Compte suivant" }),
    screenKey("ONGLET_PRECEDENT", "Alt+PageUp", "Onglet précédent", "Navigation"),
    screenKey("ONGLET_SUIVANT", "Alt+PageDown", "Onglet suivant", "Navigation"),
    verb("FERMER", { label: "Fermer le tiroir" }),
  ]),
  // Trésorerie TWP-20 — M1 the one movement form (overlay map, UX 02 §10.10). The Type control's own
  // keys (E / D / T, ← / →) and the pickers' (Alt+↓, ↑ ↓, Entrée) belong to those widgets; Entrée in a
  // field moves to the next one and never submits. Ctrl+S saves a Brouillon (no dialog, DD-02); F9
  // asks the financial ConfirmDialog (F9 → F9); Ctrl+Entrée = the primary button; Échap asks first
  // when something was typed (the drawer's guard).
  TRESO_OPERATION_FORM: screenShortcuts("Mouvement de trésorerie", [
    verb("TIERS", { label: "Choisir le tiers", group: "Saisie" }),
    screenKey("INVERSER", "F8", "Inverser source et destination (transfert)", "Saisie", { icon: "arrow-left-right" }),
    screenKey("RETABLIR", "Ctrl+Backspace", "Rétablir la valeur d'avant (champ modifié)", "Saisie"),
    verb("ENREGISTRER", { label: "Enregistrer le brouillon", shortLabel: "Brouillon" }),
    verb("VALIDER_SAISIE", { label: "Action principale du formulaire", group: "Document" }),
    verb("FERMER", { label: "Fermer (une question si des saisies sont en cours)" }),
    verb("VALIDER", { label: "Confirmer le mouvement", shortLabel: "Confirmer" }),
  ]),
  // E-4 ConfirmPopover and the bulk confirmation (UX 02 §11.3-§11.4): Entrée confirms the popover
  // (its own key), F9 confirms the selection, Échap closes and leaves the row unticked.
  TRESO_CONFIRMER: screenShortcuts("Confirmation", [
    verb("FERMER", { label: "Annuler (la case reste décochée)" }),
    verb("VALIDER", { label: "Confirmer", shortLabel: "Confirmer" }),
  ]),
  // M3 — the operation drawer (overlay map, UX 02 §12, UX 03 §11): Ctrl+↑ / Ctrl+↓ walk the list
  // underneath, Alt+O « Modifier », F9 « Confirmer » a Brouillon (E-4), Suppr « Annuler l'opération… »,
  // Alt+H the « Historique » tab, Alt+PgUp / PgDn the tabs, Ctrl+P the A4 pièce, Échap closes the stack.
  TRESO_OPERATION_DRAWER: screenShortcuts("Opération de trésorerie", [
    sharedAction("MODIFIER", { label: "Modifier l'opération", shortLabel: "Modifier" }),
    verb("SUPPRIMER", { label: "Annuler l'opération…", group: "Document" }),
    screenKey("HISTORIQUE", "Alt+H", "Onglet Historique", "Navigation", { icon: "history" }),
    verb("PRECEDENT", { label: "Opération précédente de la liste" }),
    verb("SUIVANT", { label: "Opération suivante de la liste" }),
    screenKey("ONGLET_PRECEDENT", "Alt+PageUp", "Onglet précédent", "Navigation"),
    screenKey("ONGLET_SUIVANT", "Alt+PageDown", "Onglet suivant", "Navigation"),
    verb("IMPRIMER", { label: "Imprimer la pièce (A4)" }),
    verb("FERMER", { label: "Fermer les panneaux" }),
    verb("VALIDER", { label: "Confirmer le brouillon", shortLabel: "Confirmer" }),
  ]),
  // J1 — « Mouvements » /tresorerie/mouvements (UX 03 §9.11, TWP-29 — the full journal): F2 the search,
  // F3 « Nouveau… » (Encaissement · Décaissement · Transfert — the letter picks in the menu), F4 the tiers
  // filter, F8 the period (the ContexteBar's key, UX 01 §5 — F7 « Périmètre » comes with it, TWP-37),
  // Maj+F2 « Plus de filtres », Maj+F8 the view's default filters, Alt+PgUp / PgDn the views, Alt+O
  // « Modifier » and Suppr « Annuler l'opération… » of the focused row (the server's verdict, said in
  // the drawer), Alt+X the export and Ctrl+P the A4 print of the filtered set; in « À confirmer »
  // Espace ticks a Brouillon (the grid's key) and F9 confirms the selection. The grid's keys (↑ ↓,
  // Entrée « Ouvrir », Maj+F10 the row menu) belong to the DataGrid.
  TRESO_JOURNAL: screenShortcuts("Mouvements", [
    verb("RECHERCHER", { label: "Rechercher dans le journal (N°, référence, motif, tiers, montant)", shortLabel: "Rechercher" }),
    verb("NOUVEAU", { label: "Nouveau… (encaissement, décaissement, transfert)", shortLabel: "Nouveau…" }),
    verb("TIERS", { label: "Filtrer par tiers", shortLabel: "Tiers", group: "Filtres" }),
    screenKey("PERIODE", "F8", "Choisir la période", "Filtres", { icon: "calendar" }),
    screenKey("PLUS_DE_FILTRES", "Shift+F2", "Plus de filtres", "Filtres", { icon: "sliders-horizontal" }),
    screenKey("REINITIALISER", "Shift+F8", "Réinitialiser les filtres", "Filtres"),
    screenKey("ONGLET_PRECEDENT", "Alt+PageUp", "Vue précédente", "Navigation"),
    screenKey("ONGLET_SUIVANT", "Alt+PageDown", "Vue suivante", "Navigation"),
    sharedAction("MODIFIER", { label: "Modifier l'opération sélectionnée", shortLabel: "Modifier" }),
    verb("SUPPRIMER", { label: "Annuler l'opération sélectionnée…", group: "Document" }),
    sharedAction("EXPORTER", { label: "Exporter le journal filtré (CSV)", shortLabel: "Exporter" }),
    verb("IMPRIMER", { label: "Imprimer le journal filtré (A4)" }),
    verb("VALIDER", { label: "Confirmer la sélection (vue « À confirmer »)", shortLabel: "Confirmer" }),
  ]),
  // E-11 « Filtres du journal » (overlay map, UX 03 §9.5 / §9.11): Ctrl+Entrée « Appliquer », Maj+F8
  // « Réinitialiser » (the view's defaults in the drawer, applied by Ctrl+Entrée), Échap closes.
  TRESO_FILTRES_JOURNAL: screenShortcuts("Filtres du journal", [
    verb("VALIDER_SAISIE", { label: "Appliquer les filtres", group: "Filtres" }),
    screenKey("REINITIALISER", "Shift+F8", "Réinitialiser les filtres", "Filtres"),
    verb("FERMER", { label: "Fermer sans appliquer" }),
  ]),
  // Trésorerie TWP-22 — M4 « Ma caisse » (the card of /caissier and of « Mouvements de ma caisse »,
  // UX 02 §3 / §13.1): E / D / T as bare letters (never in a text field, never inside a grid or a list —
  // a focused widget owns its bare keys) and Alt+T « Transférer » (TA-44: one meaning in Trésorerie,
  // it also works from the grid). In the transfer dialog the digits 1-9 choose a destination (the
  // numbered RadioGroup's own keys), F9 asks the confirmation, Échap closes.
  TRESO_CAISSIER: screenShortcuts("Ma caisse", [
    screenKey("ENCAISSER", "E", "Encaisser dans ma caisse", "Ma caisse", { icon: "arrow-down-left" }),
    screenKey("DECAISSER", "D", "Décaisser de ma caisse", "Ma caisse", { icon: "arrow-up-right" }),
    screenKey("TRANSFERER", ["T", "Alt+T"], "Transférer depuis ma caisse", "Ma caisse", { icon: "arrow-right-left" }),
  ]),
  // C3-C6 — the account forms (UX 02 §6.4): Entrée moves field to field and never submits; Ctrl+S or
  // Ctrl+Entrée creates (a financial commit asks through its ConfirmDialog, F9 there).
  TRESO_COMPTE_FORM: screenShortcuts("Formulaire de compte", [
    verb("ENREGISTRER", { label: "Créer le compte / enregistrer les modifications", shortLabel: "Enregistrer" }),
    verb("VALIDER_SAISIE", { label: "Créer le compte / enregistrer les modifications", group: "Document" }),
    verb("FERMER", { label: "Fermer (une question si des saisies sont en cours)" }),
  ]),
  // C6 — the « Journée » tab of a comptoir (UX 02 §9.4): Alt+Maj+← / → walk the days (Alt+← / Alt+→
  // stay the browser's back / forward), Alt+Z prints the Z of the day's closed session.
  TRESO_COMPTOIR_JOURNEE: screenShortcuts("Journée du comptoir", [
    screenKey("JOUR_PRECEDENT", "Alt+Shift+ArrowLeft", "Jour précédent", "Navigation"),
    screenKey("JOUR_SUIVANT", "Alt+Shift+ArrowRight", "Jour suivant", "Navigation"),
    screenKey("RAPPORT_Z", "Alt+Z", "Rapport Z de la session clôturée", "Document", { icon: "printer" }),
  ]),
  // Refonte WP-20 — the one Paramètres workspace (UX OP-UX-01; Trésorerie UX 02 §15 reuses it:
  // Ctrl+S per section, Ctrl+Retour arrière on the focused setting, Alt+Pg préc. / Pg suiv.).
  PARAMETRES: screenShortcuts("Paramètres", [
    verb("ENREGISTRER", { label: "Enregistrer la section", shortLabel: "Enregistrer" }),
    screenKey(
      "RETABLIR_DEFAUT",
      "Ctrl+Backspace",
      "Rétablir la valeur par défaut du paramètre sélectionné",
      "Document",
    ),
    screenKey("SECTION_PRECEDENTE", "Alt+PageUp", "Section précédente", "Navigation"),
    screenKey("SECTION_SUIVANTE", "Alt+PageDown", "Section suivante", "Navigation"),
  ]),
  // Trésorerie TWP-23 — P1 « Motifs » (UX 02 §3 `TRESO_MOTIFS`, §15.4), registered by the Paramètres
  // page while the tab is active (`usePublicationSection` `ecran`); it keeps the workspace's section
  // keys so the F1 sheet documents everything that fires. F2 the search, F3 « Nouveau motif », F7
  // « Nouveau groupe » (not Alt+G, which means Régler — 02R §2.3), Alt+O « Modifier », Suppr
  // « Désactiver… » / « Réactiver » (inside the grid, the grid's own key through its confirmation),
  // Alt+Maj+↑ / ↓ move the motif within its group (same family as Alt+Maj+← / → of the Journée).
  TRESO_MOTIFS: screenShortcuts("Motifs de trésorerie", [
    verb("RECHERCHER", { label: "Rechercher un motif", shortLabel: "Rechercher" }),
    verb("NOUVEAU", { label: "Nouveau motif", shortLabel: "Nouveau motif" }),
    screenKey("NOUVEAU_GROUPE", "F7", "Nouveau groupe", "Motifs", { icon: "folder-plus" }),
    sharedAction("MODIFIER", { label: "Modifier le motif sélectionné", shortLabel: "Modifier" }),
    verb("SUPPRIMER", { label: "Désactiver / réactiver le motif sélectionné", group: "Motifs" }),
    screenKey("MONTER", "Alt+Shift+ArrowUp", "Monter le motif dans son groupe", "Motifs", { icon: "arrow-up" }),
    screenKey("DESCENDRE", "Alt+Shift+ArrowDown", "Descendre le motif dans son groupe", "Motifs", { icon: "arrow-down" }),
    screenKey("SECTION_PRECEDENTE", "Alt+PageUp", "Section précédente", "Navigation"),
    screenKey("SECTION_SUIVANTE", "Alt+PageDown", "Section suivante", "Navigation"),
  ]),
  // The motif drawer (overlay map, UX 02 §15.4: create · rename · re-type · move): Entrée or Ctrl+S
  // saves (not a financial form), Ctrl+Entrée = the primary button, Échap closes (asking when typed).
  TRESO_MOTIF_FORM: screenShortcuts("Motif de trésorerie", [
    verb("ENREGISTRER", { label: "Enregistrer le motif", shortLabel: "Enregistrer" }),
    verb("VALIDER_SAISIE", { label: "Enregistrer le motif", group: "Document" }),
    verb("FERMER", { label: "Fermer (une question si des saisies sont en cours)" }),
  ]),
  // Refonte WP-80 — « Paramètres › Achats › Motifs des réclamations et avoirs » (CP §7, D-37), registered
  // by the Paramètres page while the section is active (`usePublicationSection` `ecran`, the TRESO_MOTIFS
  // pattern): F2 the search, F3 « Nouveau motif », Alt+O « Modifier » (« Consulter » an archived one),
  // Suppr « Désactiver… » / « Réactiver… » (inside the grid, the grid's own key); « Archiver… » and
  // « Supprimer… » from the row menu (Maj+F10). The onglets Réclamations / Avoirs produits are the
  // frame's Tabs (← / →); Alt+Pg préc. / Pg suiv. keep walking the sections.
  MOTIFS_FOURNISSEUR: screenShortcuts("Motifs des réclamations et avoirs", [
    verb("RECHERCHER", { label: "Rechercher un motif", shortLabel: "Rechercher" }),
    verb("NOUVEAU", { label: "Nouveau motif", shortLabel: "Nouveau motif" }),
    sharedAction("MODIFIER", { label: "Modifier le motif sélectionné", shortLabel: "Modifier" }),
    verb("SUPPRIMER", { label: "Désactiver / réactiver le motif sélectionné", group: "Motifs" }),
    screenKey("SECTION_PRECEDENTE", "Alt+PageUp", "Section précédente", "Navigation"),
    screenKey("SECTION_SUIVANTE", "Alt+PageDown", "Section suivante", "Navigation"),
  ]),
  // The motif drawer (overlay map): Ctrl+S or Ctrl+Entrée saves (a configuration form — no financial
  // confirmation), Entrée in a one-line field saves too, Échap closes (asking when something was typed).
  MOTIF_FOURNISSEUR_FORM: screenShortcuts("Motif des réclamations et avoirs", [
    verb("ENREGISTRER", { label: "Enregistrer le motif", shortLabel: "Enregistrer" }),
    verb("VALIDER_SAISIE", { label: "Enregistrer le motif", group: "Document" }),
    verb("FERMER", { label: "Fermer (une question si des saisies sont en cours)" }),
  ]),
  // Refonte WP-67 (M-23; D-30, A-77) — « Paramètres › Achats et étiquettes › Types de produits », registered
  // by the Paramètres page while the section is active (`usePublicationSection` `ecran`, the TRESO_MOTIFS
  // pattern): F2 the search, F3 « Nouveau type », Alt+O « Modifier », Suppr « Désactiver… » / « Réactiver »
  // (inside the grid, the grid's own key), Alt+Maj+↑ / ↓ move the type in the list, Alt+Pg préc. / Pg suiv.
  // the workspace's sections.
  TYPES_PRODUITS: screenShortcuts("Types de produits", [
    verb("RECHERCHER", { label: "Rechercher un type", shortLabel: "Rechercher" }),
    verb("NOUVEAU", { label: "Nouveau type de produit", shortLabel: "Nouveau type" }),
    sharedAction("MODIFIER", { label: "Modifier le type sélectionné", shortLabel: "Modifier" }),
    verb("SUPPRIMER", { label: "Désactiver / réactiver le type sélectionné", group: "Types de produits" }),
    screenKey("MONTER", "Alt+Shift+ArrowUp", "Monter le type dans la liste", "Types de produits", { icon: "arrow-up" }),
    screenKey("DESCENDRE", "Alt+Shift+ArrowDown", "Descendre le type dans la liste", "Types de produits", { icon: "arrow-down" }),
    screenKey("SECTION_PRECEDENTE", "Alt+PageUp", "Section précédente", "Navigation"),
    screenKey("SECTION_SUIVANTE", "Alt+PageDown", "Section suivante", "Navigation"),
  ]),
  // The type drawer (overlay map): Entrée or Ctrl+S saves (not a financial form), Ctrl+Entrée = the
  // primary button, Échap closes (asking when something was typed).
  TYPE_PRODUIT_FORM: screenShortcuts("Type de produit", [
    verb("ENREGISTRER", { label: "Enregistrer le type", shortLabel: "Enregistrer" }),
    verb("VALIDER_SAISIE", { label: "Enregistrer le type", group: "Document" }),
    verb("FERMER", { label: "Fermer (une question si des saisies sont en cours)" }),
  ]),
  // Trésorerie TWP-26 — G1 « Soldes fournisseurs » /tresorerie/reglements/soldes (UX 03 §3, 03_UX §4.3).
  // F2 the search, F3 « Régler un fournisseur » (G2, no fournisseur yet), Alt+G « Régler » the focused
  // row (« Préparer un règlement » for a manager without the TD-45 setting — TD-187), Maj+F8 the view's
  // default filters, Alt+PgUp / PgDn the tabs. The grid's keys (↑ ↓, Entrée « Relevé », Maj+F10 the row
  // menu) belong to the DataGrid.
  TRESO_REGLEMENTS_SOLDES: screenShortcuts("Soldes fournisseurs", [
    verb("RECHERCHER", { label: "Rechercher un fournisseur", shortLabel: "Rechercher" }),
    verb("NOUVEAU", { label: "Régler un fournisseur", shortLabel: "Régler un fournisseur" }),
    screenKey("REGLER", "Alt+G", "Régler le fournisseur sélectionné", "Règlements", { icon: "hand-coins" }),
    screenKey("REINITIALISER", "Shift+F8", "Réinitialiser les filtres", "Navigation"),
    screenKey("ONGLET_PRECEDENT", "Alt+PageUp", "Onglet précédent", "Navigation"),
    screenKey("ONGLET_SUIVANT", "Alt+PageDown", "Onglet suivant", "Navigation"),
  ]),
  // G2 — « Régler un fournisseur », the settlement presentation of the one movement form (overlay map,
  // UX 03 §4.10): F4 the fournisseur, Entrée in « Montant » asks « Voulez-vous ventiler ce montant ? »
  // (the field's own key), Alt+V the ventilation control (then A / S / M, its own letters), F7 the
  // payment fields (expand / collapse), F8 « Montant = total de la sélection / ventilé », Ctrl+Retour
  // arrière the proposed value of the focused cell (mode Par facture), Ctrl+S the Brouillon, F9 the
  // financial confirmation (F9 → F9), Ctrl+Entrée the primary button, Échap (asks once typed). The grid's
  // keys (Espace cocher, chiffre saisir, Suppr vider, Entrée) belong to the VentilationGrid.
  TRESO_REGLEMENT_FORM: screenShortcuts("Règlement fournisseur", [
    verb("TIERS", { label: "Choisir le fournisseur", group: "Saisie" }),
    screenKey("MODE_VENTILATION", "Alt+V", "Mode de ventilation (puis A, S ou M)", "Ventilation", { icon: "split" }),
    screenKey("DETAILS_PAIEMENT", "F7", "Détails du paiement (afficher / réduire)", "Saisie"),
    screenKey("LIER_MONTANT", "F8", "Montant = total de la sélection / total ventilé", "Ventilation"),
    screenKey("RETABLIR", "Ctrl+Backspace", "Rétablir la valeur proposée (cellule)", "Ventilation"),
    verb("ENREGISTRER", { label: "Enregistrer le brouillon", shortLabel: "Brouillon" }),
    verb("VALIDER_SAISIE", { label: "Action principale du formulaire", group: "Document" }),
    verb("FERMER", { label: "Fermer (une question si des saisies sont en cours)" }),
    verb("VALIDER", { label: "Confirmer le règlement", shortLabel: "Confirmer" }),
  ]),
  // TD-43 — « Imputer le crédit » (overlay map, UX 03 §4.11): the G2 ventilation keys without the
  // payment ones — Alt+V the ventilation control (then A / S / M), F8 « Montant = crédit imputable »,
  // Ctrl+Retour arrière the proposed value of the focused cell (Par facture), F9 the confirmation
  // (F9 → F9), Ctrl+Entrée the primary button, Échap (asks once typed).
  TRESO_IMPUTER_CREDIT: screenShortcuts("Imputer le crédit fournisseur", [
    screenKey("MODE_VENTILATION", "Alt+V", "Mode d'imputation (puis A, S ou M)", "Ventilation", { icon: "split" }),
    screenKey("LIER_MONTANT", "F8", "Montant = crédit imputable", "Ventilation"),
    screenKey("RETABLIR", "Ctrl+Backspace", "Rétablir la valeur proposée (cellule)", "Ventilation"),
    verb("VALIDER_SAISIE", { label: "Action principale", group: "Document" }),
    verb("FERMER", { label: "Fermer (une question si des saisies sont en cours)" }),
    verb("VALIDER", { label: "Imputer le crédit", shortLabel: "Imputer" }),
  ]),
  // TWP-56 — « Imputer l'avoir » (overlay map over « Imputer le crédit », the UI of TWP-57): the grid's own
  // keys (↑ ↓, a digit or Entrée edits « Imputé », Entrée commits and moves down), Ctrl+Retour arrière the
  // proposed value of the focused row, F9 the confirmation (F9 → F9), Ctrl+Entrée the primary button,
  // Échap (asks once a part is changed).
  TRESO_IMPUTER_AVOIR: screenShortcuts("Imputer le crédit d'un avoir", [
    screenKey("RETABLIR", "Ctrl+Backspace", "Rétablir le montant proposé (ligne)", "Imputation"),
    verb("VALIDER_SAISIE", { label: "Action principale", group: "Document" }),
    verb("FERMER", { label: "Fermer (une question si un montant a été changé)" }),
    verb("VALIDER", { label: "Imputer le crédit de l'avoir", shortLabel: "Imputer" }),
  ]),
  // G3 — « Journal des règlements » /tresorerie/reglements/journal (UX 03 §5): as the journal, F2 the
  // search, F3 « Régler un fournisseur », → / ← the imputations of the row (the grid's detail keys),
  // Entrée the operation drawer on « Ventilation », Ctrl+P the A4 « Avis de règlement » of the row.
  TRESO_JOURNAL_REGLEMENTS: screenShortcuts("Journal des règlements", [
    verb("RECHERCHER", { label: "Rechercher un règlement", shortLabel: "Rechercher" }),
    verb("NOUVEAU", { label: "Régler un fournisseur", shortLabel: "Régler un fournisseur" }),
    verb("IMPRIMER", { label: "Imprimer l'avis de règlement de la ligne" }),
    sharedAction("MODIFIER", { label: "Modifier le règlement de la ligne", shortLabel: "Modifier" }),
    screenKey("REINITIALISER", "Shift+F8", "Réinitialiser les filtres", "Navigation"),
    screenKey("ONGLET_PRECEDENT", "Alt+PageUp", "Onglet précédent", "Navigation"),
    screenKey("ONGLET_SUIVANT", "Alt+PageDown", "Onglet suivant", "Navigation"),
  ]),
  // Trésorerie TWP-27 — G4 « Charges » /tresorerie/reglements/charges (UX 03 §13.2, §18): F2 the search,
  // F3 « Nouvelle charge », Alt+G « Payer » the focused row (the « settle » family), Alt+O « Modifier »,
  // Suppr « Annuler la charge… », Maj+F8 the view's default filters, Alt+PgUp / PgDn the tabs. The grid's
  // keys (↑ ↓, Entrée « Ouvrir », Maj+F10 the row menu) belong to the DataGrid.
  TRESO_CHARGES: screenShortcuts("Charges", [
    verb("RECHERCHER", { label: "Rechercher une charge", shortLabel: "Rechercher" }),
    verb("NOUVEAU", { label: "Nouvelle charge", shortLabel: "Nouvelle charge" }),
    screenKey("PAYER", "Alt+G", "Payer la charge sélectionnée", "Charges", { icon: "hand-coins" }),
    sharedAction("MODIFIER", { label: "Modifier la charge sélectionnée", shortLabel: "Modifier" }),
    verb("SUPPRIMER", { label: "Annuler la charge sélectionnée…", group: "Charges" }),
    screenKey("REINITIALISER", "Shift+F8", "Réinitialiser les filtres", "Navigation"),
    screenKey("ONGLET_PRECEDENT", "Alt+PageUp", "Onglet précédent", "Navigation"),
    screenKey("ONGLET_SUIVANT", "Alt+PageDown", "Onglet suivant", "Navigation"),
  ]),
  // « Nouvelle charge / Modifier » (overlay map, UX 03 §13.3): Entrée the next field (the form's own
  // key), Ctrl+S « Enregistrer la charge » (a document, not money: no financial dialog), Ctrl+Entrée the
  // primary button, Alt+Entrée « Créer le créancier » (the Combobox's own key), Échap (asks once typed).
  TRESO_CHARGE_FORM: screenShortcuts("Charge", [
    verb("ENREGISTRER", { label: "Enregistrer la charge", shortLabel: "Enregistrer" }),
    verb("VALIDER_SAISIE", { label: "Enregistrer la charge", group: "Document" }),
    verb("FERMER", { label: "Fermer (une question si des saisies sont en cours)" }),
  ]),
  // The charge drawer (overlay map, UX 03 §13.5): Alt+G « Payer », Alt+O « Modifier », Suppr « Annuler la
  // charge… » (refused with its reason while a payment holds it), Échap closes the stack.
  TRESO_CHARGE_DRAWER: screenShortcuts("Charge (tiroir)", [
    screenKey("PAYER", "Alt+G", "Payer la charge", "Charges", { icon: "hand-coins" }),
    sharedAction("MODIFIER", { label: "Modifier la charge", shortLabel: "Modifier" }),
    verb("SUPPRIMER", { label: "Annuler la charge…", group: "Charges" }),
    verb("FERMER", { label: "Fermer les panneaux" }),
  ]),
  // G6 — the facture drawer (overlay map, UX 03 §8.2): Alt+G « Régler » what remains (the « settle »
  // family, refonte S3 meaning), Entrée on a row opens it (the grid's key), Échap closes the stack.
  TRESO_FACTURE_DRAWER: screenShortcuts("Facture d'achat (tiroir)", [
    screenKey("REGLER", "Alt+G", "Régler la facture", "Règlements", { icon: "hand-coins" }),
    verb("FERMER", { label: "Fermer les panneaux" }),
  ]),
  // T2 — the fournisseur drawer (overlay map, UX 03 §14.5): Alt+G « Régler la sélection » (or « Régler »
  // when nothing is checked), Alt+PgUp / PgDn the tabs, Ctrl+P « Imprimer le relevé », Échap. Espace /
  // Ctrl+A on « Factures ouvertes » belong to the grid.
  TRESO_TIERS_DRAWER: screenShortcuts("Tiroir du fournisseur", [
    screenKey("REGLER", "Alt+G", "Régler la sélection (ou le fournisseur)", "Règlements", { icon: "hand-coins" }),
    verb("IMPRIMER", { label: "Imprimer le relevé (A4)" }),
    screenKey("ONGLET_PRECEDENT", "Alt+PageUp", "Onglet précédent", "Navigation"),
    screenKey("ONGLET_SUIVANT", "Alt+PageDown", "Onglet suivant", "Navigation"),
    verb("FERMER", { label: "Fermer les panneaux" }),
  ]),
});

/**
 * Group bindings for the F1 sheet, first-seen group order kept (the
 * generalised `posScreenShortcutGroups`).
 * @param {ReadonlyArray<{group?: string}>} entries
 */
export function shortcutGroups(entries) {
  /** @type {Map<string, any[]>} */
  const grouped = new Map();
  for (const s of entries ?? []) {
    const g = s.group ?? "Raccourcis";
    if (!grouped.has(g)) grouped.set(g, []);
    grouped.get(g).push(s);
  }
  return Array.from(grouped.entries()).map(([group, shortcuts]) => ({ group, shortcuts }));
}

export default POS_SHORTCUTS;
